import React, { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { AnimatePresence, motion } from "framer-motion";
import InputMask from "react-input-mask";
import {
  Alert,
  Button,
  Label,
  Select,
  Spinner,
  Textarea,
  TextInput,
} from "flowbite-react";
import axios from "../config/axiosInstance";
import { LOAN } from "../config/apiConfig";
import { HiCheckCircle } from "react-icons/hi";
import "./FormPage.css";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

const parseUTMFromSearch = (search) => {
  if (!search) return {};
  const params = new URLSearchParams(search);
  return UTM_KEYS.reduce((acc, key) => {
    const value = params.get(key);
    if (value) acc[key] = value;
    return acc;
  }, {});
};

const getFieldName = (field) => `${field.source}__${field.key}`;

const getFieldInputType = (field) => {
  if (field.source === "lead") {
    if (field.key === "notes") return "textarea";
    if (field.key === "email") return "email";
    if (field.key === "phoneNumber") return "tel";
    return "text";
  }
  const normalized = (field.type || "").toLowerCase();
  if (normalized === "textarea") return "textarea";
  if (["number", "currency", "percent"].includes(normalized)) return "number";
  if (["date"].includes(normalized)) return "date";
  if (["datetime"].includes(normalized)) return "datetime-local";
  if (["single_select", "select"].includes(normalized)) return "select";
  if (["multi_select"].includes(normalized)) return "multiselect";
  if (["radio"].includes(normalized)) return "radio";
  if (["checkbox", "boolean"].includes(normalized)) return "checkbox";
  return "text";
};

const getFieldMask = (field) => {
  const key = String(field.key || "").toLowerCase();
  const combined = `${field.source || ""}:${key}`;
  if (combined.includes("phone")) {
    return "(99) 99999-9999";
  }
  if (key.includes("cpf")) {
    return "999.999.999-99";
  }
  if (key.includes("cnpj")) {
    return "99.999.999/9999-99";
  }
  return null;
};

const getValidationRules = (field, inputType) => {
  if (!field.required) return {};
  if (inputType === "multiselect") {
    return {
      validate: (value) =>
        Array.isArray(value) && value.length > 0
          ? true
          : "Selecione ao menos uma opção.",
    };
  }
  return {
    required: `${field.label || "Campo"} é obrigatório.`,
  };
};

const FormPage = () => {
  const { slug } = useParams();
  const { search } = useLocation();
  const utmParams = useMemo(() => parseUTMFromSearch(search), [search]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const {
    data: formData,
    isLoading: isFormLoading,
    error: formError,
  } = useQuery(
    ["public-form", slug],
    async () => {
      const response = await axios.get(LOAN.PUBLIC_FORM(slug));
      return response.data.data;
    },
    {
      enabled: Boolean(slug),
      retry: false,
    }
  );

  const formFields = useMemo(
    () =>
      (formData?.fields || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [formData]
  );

  const mutation = useMutation(
    async (payload) => {
      const response = await axios.post(LOAN.PUBLIC_FORM(slug), payload);
      return response.data.data;
    },
    {
      onSuccess: () => {
        reset();
      },
    }
  );

  const handleStartNewSubmission = () => {
    reset();
    mutation.reset();
  };

  const stageName = formData?.stage?.name;
  const stageMessage = stageName
    ? `O lead será criado direto na etapa ${stageName}.`
    : "O time do CRM já começa a tratar este lead a partir de agora.";
  const headerLabel = formData?.company?.name || "Formulário";
  const headerTitle = formData?.title || "Preencha o formulário";
  const headerDescription =
    formData?.description ||
    "Conte-nos rapidamente suas necessidades para receber um retorno personalizado.";

  const renderField = (field) => {
    const fieldName = getFieldName(field);
    const inputType = getFieldInputType(field);
    const validationRules = getValidationRules(field, inputType);
    const registerProps = register(fieldName, validationRules);
    const placeholder =
      field.placeholder ||
      field.definition?.placeholder ||
      `Digite ${field.label || "o valor"}`;
    const options = field.definition?.options || [];
    const helperText = field.definition?.description || null;
    const errorMessage = errors[fieldName]?.message;

    const fieldLabel = field.label || field.definition?.label || "Campo";

    const renderMaskedTextInput = (mask) => {
      const { ref, ...restRegister } = registerProps;
      return (
        <InputMask mask={mask} maskChar={null} {...restRegister}>
          {(inputProps) => (
            <TextInput
              {...inputProps}
              id={fieldName}
              placeholder={placeholder}
              className="form-page-input"
              ref={(node) => {
                inputProps.ref?.(node);
                if (typeof ref === "function") {
                  ref(node);
                } else if (ref) {
                  ref.current = node;
                }
              }}
            />
          )}
        </InputMask>
      );
    };

    const fieldInner = (() => {
      switch (inputType) {
        case "textarea":
          return (
            <Textarea
              {...registerProps}
              id={fieldName}
              placeholder={placeholder}
              rows={4}
              className="form-page-input form-page-textarea"
            />
          );
        case "select":
          return (
            <Select
              {...registerProps}
              id={fieldName}
              placeholder={placeholder}
              className="form-page-input"
            >
              <option value="">Selecione</option>
              {options.map((option) => (
                <option
                  key={`${fieldName}-${option.value}`}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </Select>
          );
        case "radio":
          return (
            <div className="form-page-radio-group">
              {options.map((option) => (
                <label
                  key={`${fieldName}-radio-${option.value}`}
                  className="form-page-radio-option"
                >
                  <input
                    type="radio"
                    value={option.value}
                    {...registerProps}
                    className="form-page-radio-input"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          );
        case "multiselect":
          return (
            <div className="form-page-multiselect">
              {options.map((option) => (
                <label
                  key={`${fieldName}-checkbox-${option.value}`}
                  className="form-page-multiselect-option"
                >
                  <input
                    type="checkbox"
                    value={option.value}
                    {...registerProps}
                    className="form-page-checkbox-input"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          );
        case "checkbox":
          return (
            <label className="form-page-checkbox-label">
              <input
                type="checkbox"
                {...registerProps}
                className="form-page-checkbox-input"
              />
              <span>{field.definition?.label || fieldLabel}</span>
            </label>
          );
        default:
          const mask = getFieldMask(field);
          if (mask) {
            return renderMaskedTextInput(mask);
          }
          return (
            <TextInput
              {...registerProps}
              id={fieldName}
              type={inputType}
              placeholder={placeholder}
              className="form-page-input"
            />
          );
      }
    })();

    return (
      <div key={fieldName} className="form-page-field">
        {inputType !== "checkbox" && (
          <div className="form-page-field-header">
            <Label
              htmlFor={fieldName}
              value={fieldLabel}
              className="form-page-field-label"
            />
            {field.required && (
              <span className="form-page-required-badge">(*)</span>
            )}
          </div>
        )}
        {fieldInner}
        {helperText && <p className="form-page-helper">{helperText}</p>}
        {errorMessage && <p className="form-page-error">{errorMessage}</p>}
      </div>
    );
  };

  const handleSubmitForm = (values) => {
    if (!slug) return;
    const payload = {
      source: `website`,
      fields: formFields.map((field) => ({
        source: field.source,
        key: field.key,
        value: values[getFieldName(field)],
      })),
      utm: Object.keys(utmParams).length ? utmParams : null,
    };
    mutation.mutate(payload);
  };

  const submissionError =
    mutation.error?.response?.data?.message || mutation.error?.message;

  return (
    <div className="form-page-root">
      <div className="form-page-glow" />
      <div className="form-page-card">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="form-page-header"
        >
          <p className="form-page-label">{headerLabel}</p>
          <h1 className="form-page-title">{headerTitle}</h1>
          {headerDescription && (
            <p className="form-page-description">{headerDescription}</p>
          )}
          <div className="form-page-header-glow">
            <span />
            <span />
          </div>
        </motion.div>

        <div className="form-page-body">
          {isFormLoading && (
            <div className="form-page-loading">
              <Spinner size="lg" aria-label="Carregando formulário" />
            </div>
          )}

          <AnimatePresence mode="wait">
            {mutation.isSuccess ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                transition={{ duration: 0.5 }}
                className="form-page-success"
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="form-page-success-icon"
                >
                  <HiCheckCircle fontSize={40} />
                </motion.div>
                <h2 className="form-page-success-title">Tudo certo! 🎉</h2>
                <p className="form-page-success-subtitle">
                  Seu formulário foi enviado com sucesso. Agora é com a gente.
                </p>

                <div className="form-page-success-actions">
                  <Button
                    onClick={handleStartNewSubmission}
                    color="success"
                    className="form-page-action"
                  >
                    Enviar novo formulário
                  </Button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 18 }}
                transition={{ duration: 0.5 }}
                className="form-page-formshell"
              >
                {!isFormLoading && formFields.length > 0 && (
                  <motion.form
                    className="form-page-form"
                    onSubmit={handleSubmit(handleSubmitForm)}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    {formFields.map((field) => (
                      <motion.div
                        key={getFieldName(field)}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        {renderField(field)}
                      </motion.div>
                    ))}
                    <div className="form-page-form-footer">
                      <Button
                        type="submit"
                        disabled={mutation.isLoading}
                        className="form-page-submit"
                      >
                        {mutation.isLoading
                          ? "Enviando..."
                          : "Enviar formulário"}
                      </Button>
                    </div>
                    {formError && (
                      <Alert color="failure" className="form-page-alert">
                        <p>
                          {formError.response?.data?.message ||
                            formError.message ||
                            "Não foi possível carregar este formulário."}
                        </p>
                      </Alert>
                    )}

                    {mutation.isError && (
                      <Alert color="failure" className="form-page-alert">
                        <p>
                          {submissionError ||
                            "Ocorreu um erro ao enviar o formulário."}
                        </p>
                      </Alert>
                    )}

                    {!isFormLoading && !formFields.length && !formError && (
                      <Alert color="warning" className="form-page-alert">
                        <p>Esse formulário ainda não foi configurado.</p>
                      </Alert>
                    )}
                  </motion.form>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default FormPage;
